import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from "react";
import { dmPartner } from "@shared/protocol";
import type { Message } from "@shared/types";
import { Composer } from "@/components/Composer";
import { MessageItem } from "@/components/MessageItem";
import { CloseIcon, HashIcon, MembersIcon, SettingsIcon } from "@/components/icons";
import { plainPreview } from "@/lib/richText";
import {
  activeChannel,
  activeDmUser,
  can,
  colorOf,
  messagesOf,
  typingIn,
  useApp,
  userById,
} from "@/state/store";
import { C, FONT_MONO, formatStamp, hexA, initialOf, statusLabel } from "@/theme";

/** Distancia do fim (px) que ainda conta como rolagem grudada no rodape. */
const STICK_THRESHOLD = 120;

/**
 * Distancia do topo (px) que ja pede a pagina anterior.
 *
 * Folgado de proposito: o pedido vai ao servidor e volta, e a ideia e a
 * pagina chegar antes de a rolagem bater no topo de verdade.
 */
const LOAD_OLDER_THRESHOLD = 320;

/** Tempo que a mensagem alcancada pela busca fica destacada. */
const HIGHLIGHT_MS = 2600;

/** Vermelho da linha de novas mensagens — mesmo tom de erro do app. */
const NEW_RED = C.red;

function SearchGlyph({ size = 16 }: { size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="11" cy="11" r="6.5" />
      <path d="M15.8 15.8 20 20" />
    </svg>
  );
}

function ArrowDownGlyph({ size = 13 }: { size?: number }): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 5v14" />
      <path d="M6 13.2 12 19.2l6-6" />
    </svg>
  );
}

/** Divisoria discreta antes da primeira mensagem ainda nao lida. */
function NewMessagesLine(): ReactElement {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        margin: "12px 6px 2px",
        pointerEvents: "none",
      }}
    >
      <span style={{ flex: 1, height: 1, background: hexA(C.red, 0.42) }} />
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 9.5,
          letterSpacing: "0.12em",
          color: NEW_RED,
          whiteSpace: "nowrap",
        }}
      >
        NOVAS MENSAGENS
      </span>
    </div>
  );
}

export function ChatView(): ReactElement | null {
  const { state, actions } = useApp();

  const channel = activeChannel(state);
  const dmUser = activeDmUser(state);
  const isDm = state.activeKey.startsWith("dm:");
  const isVoice = state.activeKey.startsWith("voice:");
  const messages = messagesOf(state, state.activeKey);

  const msgsRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  /** Falso quando o usuario rolou para cima: nao arrastamos a vista de volta. */
  const stickRef = useRef(true);
  const scrollKeyRef = useRef("");
  /** Quantas mensagens a lista tinha na ultima vez que foi conciliada. */
  const countRef = useRef(0);
  /** Primeira mensagem da lista, para reconhecer uma pagina que entrou ACIMA. */
  const firstIdRef = useRef<string | null>(null);
  /** Altura da lista na ultima conciliacao, para medir o quanto ela cresceu. */
  const lastHeightRef = useRef(0);
  const searchWasOpen = useRef(false);
  const [query, setQuery] = useState("");
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [atBottom, setAtBottom] = useState(true);
  /** Mensagens que chegaram enquanto a lista estava rolada para cima. */
  const [pendingNew, setPendingNew] = useState(0);

  const searchOpen = state.search.open;
  const scrollKey = `${state.activeKey}:${messages.length}`;

  // Roda antes da pintura: com `useEffect` a lista aparecia no topo por um
  // quadro antes de pular para o fim.
  useLayoutEffect(() => {
    const el = msgsRef.current;
    // Na sala de voz e na busca a lista sai do DOM: zerar a chave faz o retorno
    // cair no fim de novo.
    if (!el || searchOpen) {
      scrollKeyRef.current = "";
      return;
    }
    if (scrollKeyRef.current === scrollKey) return;
    const changedConversation = !scrollKeyRef.current.startsWith(`${state.activeKey}:`);

    // Pagina anterior: o topo da lista mudou, mas a mensagem que era o topo
    // continua ali. Nao e mensagem nova — e o passado subindo.
    const firstId = messages[0]?.id ?? null;
    const prepended =
      !changedConversation &&
      firstIdRef.current !== null &&
      firstId !== null &&
      firstId !== firstIdRef.current &&
      messages.some(item => item.id === firstIdRef.current);
    firstIdRef.current = firstId;

    if (prepended) {
      // Conteudo entrou acima do que estava na tela: sem somar a altura que
      // apareceu, a leitura salta para tras justamente enquanto se le.
      el.scrollTop += el.scrollHeight - lastHeightRef.current;
      lastHeightRef.current = el.scrollHeight;
      scrollKeyRef.current = scrollKey;
      countRef.current = messages.length;
      return;
    }

    const previousCount = changedConversation ? messages.length : countRef.current;
    scrollKeyRef.current = scrollKey;
    countRef.current = messages.length;
    lastHeightRef.current = el.scrollHeight;
    if (changedConversation) stickRef.current = true;
    if (changedConversation || stickRef.current) {
      el.scrollTop = el.scrollHeight;
      setAtBottom(true);
      setPendingNew(0);
      return;
    }
    const arrived = messages.length - previousCount;
    if (arrived > 0) setPendingNew(value => value + arrived);
  }, [scrollKey, state.activeKey, searchOpen, messages.length]);

  // Sincroniza o campo apenas na abertura; depois ele e local, senao a busca
  // automatica apagaria o que ainda esta sendo digitado.
  useEffect(() => {
    if (searchOpen && !searchWasOpen.current) {
      setQuery(state.search.query);
      searchRef.current?.focus();
    }
    searchWasOpen.current = searchOpen;
  });

  // Busca enquanto digita, sem inundar o servidor.
  useEffect(() => {
    if (!searchOpen) return;
    const trimmed = query.trim();
    if (trimmed === state.search.query) return;
    // Termo curto demais: limpa o resultado anterior em vez de deixa-lo no ar
    // rotulado com uma contagem que nao corresponde mais ao que esta escrito.
    if (trimmed.length < 2) {
      if (state.search.query.length > 0) actions.runSearch(trimmed);
      return;
    }
    const timer = window.setTimeout(() => actions.runSearch(trimmed), 320);
    return () => window.clearTimeout(timer);
  }, [query, searchOpen, state.search.query, actions]);

  // Depois de pular de um resultado, leva a mensagem para o centro da lista.
  useEffect(() => {
    if (!highlightId || searchOpen) return;
    const el = msgsRef.current?.querySelector(`[data-msg="${highlightId}"]`);
    if (el instanceof HTMLElement) {
      stickRef.current = false;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    }
    const timer = window.setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [highlightId, searchOpen, state.activeKey, messages.length]);

  // Escape e o atalho de saida da conversa: cancela edicao, resposta, seletor de
  // reacao e busca, nesta ordem. Modais tem o proprio Escape e vem antes.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (state.settingsOpen || state.switcherOpen || state.profileEditorOpen || state.profile) {
        return;
      }
      if (state.reactionPickerFor) {
        event.preventDefault();
        actions.openReactionPicker(null);
        return;
      }
      if (state.editing) {
        event.preventDefault();
        actions.startEditing(null);
        return;
      }
      if (state.replyTo) {
        event.preventDefault();
        actions.setReplyTo(null);
        return;
      }
      if (state.search.open) {
        event.preventDefault();
        actions.closeSearch();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    actions,
    state.editing,
    state.profile,
    state.profileEditorOpen,
    state.reactionPickerFor,
    state.replyTo,
    state.search.open,
    state.settingsOpen,
    state.switcherOpen,
  ]);

  const onScroll = useCallback(() => {
    const el = msgsRef.current;
    if (!el) return;
    const stuck = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD;
    stickRef.current = stuck;
    setAtBottom(value => (value === stuck ? value : stuck));
    if (stuck) setPendingNew(value => (value === 0 ? value : 0));
    // Perto do topo, puxa o que veio antes. A rolagem dispara em rajada, mas
    // quem recusa o pedido repetido e o store — aqui so se avisa que chegou.
    if (el.scrollTop < LOAD_OLDER_THRESHOLD) actions.loadOlderMessages(state.activeKey);
  }, [actions, state.activeKey]);

  const scrollToEnd = useCallback(() => {
    const el = msgsRef.current;
    if (!el) return;
    stickRef.current = true;
    setAtBottom(true);
    setPendingNew(0);
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  const onSearchKey = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        actions.closeSearch();
        return;
      }
      if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
      event.preventDefault();
      const trimmed = query.trim();
      if (trimmed.length >= 2) actions.runSearch(trimmed);
    },
    [actions, query],
  );

  const jump = useCallback(
    (message: Message) => {
      setHighlightId(message.id);
      actions.jumpToMessage(message);
    },
    [actions],
  );

  const labelOf = useCallback(
    (key: string): string => {
      if (key.startsWith("ch:")) {
        const found = state.channels.find(item => item.id === key.slice(3));
        return found ? `#${found.name}` : "Canal";
      }
      const partner = state.me ? dmPartner(key, state.me.id) : null;
      const user = partner ? userById(state, partner) : undefined;
      return user ? `Direta com ${user.name}` : "Direta";
    },
    [state],
  );

  // O seletor do store ja descarta quem parou de digitar e voce mesmo.
  const typingNames = useMemo(() => typingIn(state, state.activeKey), [state]);

  /**
   * Indice da primeira mensagem nao lida da conversa. So vale a pena desenhar a
   * linha quando existe algo lido acima dela; a propria mensagem do membro
   * tambem nao conta como novidade.
   */
  const dividerIndex = useMemo(() => {
    const since = state.newSince[state.activeKey] ?? 0;
    if (since <= 0) return -1;
    const meId = state.me?.id;
    const index = messages.findIndex(
      item => item.createdAt > since && item.authorId !== meId && !item.deleted,
    );
    return index <= 0 ? -1 : index;
  }, [messages, state.newSince, state.activeKey, state.me]);

  if (isVoice) return null;

  const dmColor = dmUser ? colorOf(state, dmUser.id) : state.settings.accent;
  const title = dmUser ? dmUser.name : channel ? `#${channel.name}` : "";
  const topic = dmUser ? statusLabel(dmUser.status) : channel ? channel.topic : "";
  const introTitle = dmUser
    ? `Conversa com ${dmUser.name}`
    : channel
      ? `Bem-vindo a #${channel.name}`
      : "";
  const introSub = dmUser
    ? `Mensagens diretas entre você e ${dmUser.name}.`
    : channel
      ? `Este é o começo do canal. ${channel.topic}`
      : "";
  const placeholder = dmUser
    ? `Mensagem para ${dmUser.name}`
    : channel
      ? `Mensagem em #${channel.name}`
      : "Mensagem";
  /** Uma pagina anterior esta a caminho. */
  const carregandoAntigas = Boolean(state.historyLoading[state.activeKey]);
  /**
   * O cabecalho de "aqui comeca o canal" so e verdade quando o comeco esta
   * mesmo na tela. Com historico paginado ele passou a mentir: aparecia no
   * topo das ultimas 200 mensagens, com anos de conversa escondidos acima.
   */
  const noComeco = state.historyHasMore[state.activeKey] === false;
  const showIntro = (Boolean(dmUser) || Boolean(channel)) && noComeco;
  const canWrite = can(state, "mensagens");

  const emptyHint = !canWrite
    ? "Seu cargo ainda não permite escrever aqui. Fale com quem cuida dos cargos da comunidade."
    : dmUser
      ? `Escreva a primeira mensagem para ${dmUser.name}. Esta conversa fica só entre vocês dois.`
      : channel
        ? `Escreva a primeira mensagem em #${channel.name} e puxe assunto com a crew.`
        : "Escreva a primeira mensagem.";

  const typingLabel =
    typingNames.length === 0
      ? ""
      : typingNames.length === 1
        ? `${typingNames[0]} está digitando…`
        : typingNames.length === 2
          ? `${typingNames[0]} e ${typingNames[1]} estão digitando…`
          : "Vários membros estão digitando…";

  const hasNew = pendingNew > 0;
  const jumpLabel = hasNew
    ? pendingNew === 1
      ? "1 NOVA MENSAGEM"
      : `${pendingNew} NOVAS MENSAGENS`
    : "VOLTAR AO FIM";

  // O container de 1b1b1b (flex coluna) e responsabilidade do layout em App.tsx.
  return (
    <>
      <div
        style={{
          height: 54,
          flex: "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "0 16px",
          borderBottom: `1px solid ${C.line}`,
        }}
      >
        {isDm && dmUser ? (
          dmUser.avatarUrl ? (
            <img
              src={dmUser.avatarUrl}
              alt=""
              style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }}
            />
          ) : (
            <span
              style={{
                width: 24,
                height: 24,
                display: "grid",
                placeItems: "center",
                borderRadius: "50%",
                fontSize: 10.5,
                fontWeight: 600,
                background: hexA(dmColor, 0.18),
                color: dmColor,
              }}
            >
              {initialOf(dmUser.name)}
            </span>
          )
        ) : (
          <HashIcon size={18} color={C.textFaint} />
        )}
        <span style={{ fontSize: 15, fontWeight: 700 }}>{title}</span>
        <span style={{ width: 1, height: 18, background: C.lineStrong }} />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12.5,
            color: C.textDim,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {topic}
        </span>
        <button
          type="button"
          className="hov-bg-26"
          onClick={() => (searchOpen ? actions.closeSearch() : actions.openSearch())}
          title="Buscar mensagens"
          style={{
            width: 30,
            height: 30,
            display: "grid",
            placeItems: "center",
            border: 0,
            borderRadius: 8,
            cursor: "pointer",
            background: searchOpen ? C.selected : "transparent",
            color: searchOpen ? C.text : C.textMuted,
          }}
        >
          <SearchGlyph />
        </button>
        {!isDm && (
          <button
            type="button"
            className="hov-bg-26"
            onClick={actions.toggleMembers}
            title="Membros"
            style={{
              width: 30,
              height: 30,
              display: "grid",
              placeItems: "center",
              border: 0,
              borderRadius: 8,
              cursor: "pointer",
              background: state.membersOpen ? C.selected : "transparent",
              color: state.membersOpen ? C.text : C.textMuted,
            }}
          >
            <MembersIcon size={16} color="currentColor" />
          </button>
        )}
        <button
          type="button"
          className="hov-bg-26-fg"
          onClick={actions.openSettings}
          title="Configurações da comunidade"
          style={{
            width: 30,
            height: 30,
            display: "grid",
            placeItems: "center",
            border: 0,
            borderRadius: 8,
            cursor: "pointer",
            background: "transparent",
            color: C.textMuted,
          }}
        >
          <SettingsIcon size={16} color="currentColor" bg={C.stage} />
        </button>
      </div>

      {searchOpen ? (
        <div style={{ flex: 1, overflowY: "auto", padding: "14px 20px 16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 6px 6px 12px",
              border: `1px solid ${C.lineStrong}`,
              borderRadius: 12,
              background: C.raised,
            }}
          >
            <span style={{ color: C.textFaint, display: "grid", placeItems: "center" }}>
              <SearchGlyph size={15} />
            </span>
            <input
              ref={searchRef}
              value={query}
              onChange={event => setQuery(event.target.value)}
              onKeyDown={onSearchKey}
              placeholder="Buscar nas mensagens"
              style={{
                flex: 1,
                minWidth: 0,
                border: 0,
                outline: 0,
                background: "transparent",
                color: C.text,
                fontSize: 14.5,
                padding: "7px 0",
              }}
            />
            <button
              type="button"
              className="hov-bg-24"
              onClick={actions.closeSearch}
              title="Fechar busca"
              aria-label="Fechar busca"
              style={{
                width: 30,
                height: 30,
                flex: "0 0 auto",
                display: "grid",
                placeItems: "center",
                border: 0,
                borderRadius: 8,
                cursor: "pointer",
                background: "transparent",
                color: C.textMuted,
              }}
            >
              <CloseIcon size={13} color="currentColor" />
            </button>
          </div>

          <div
            style={{
              margin: "16px 4px 10px",
              fontFamily: FONT_MONO,
              fontSize: 9.5,
              letterSpacing: "0.12em",
              color: C.textLabel,
            }}
          >
            {state.search.loading
              ? "BUSCANDO…"
              : state.search.query.trim().length === 0
                ? "DIGITE PARA BUSCAR"
                : `RESULTADOS — ${state.search.results.length}`}
          </div>

          {!state.search.loading && state.search.query.trim().length === 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 11,
                padding: "10px 12px",
                border: `1px solid ${C.lineSoft}`,
                borderRadius: 12,
              }}
            >
              <span
                style={{
                  flex: "0 0 auto",
                  marginTop: 1,
                  color: C.textLabelDim,
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <SearchGlyph size={16} />
              </span>
              <span style={{ minWidth: 0 }}>
                <span
                  style={{ display: "block", fontSize: 14, fontWeight: 600, color: C.textSoft }}
                >
                  Procure em tudo que você pode ver
                </span>
                <span
                  style={{
                    display: "block",
                    marginTop: 4,
                    fontSize: 13,
                    lineHeight: 1.55,
                    color: C.textDim,
                  }}
                >
                  Canais e suas conversas diretas. Escreva pelo menos 2 caracteres — o resultado
                  aparece sozinho enquanto você digita.
                </span>
              </span>
            </div>
          )}

          {!state.search.loading &&
            state.search.query.trim().length > 0 &&
            state.search.results.length === 0 && (
              <div
                style={{
                  padding: "12px 14px",
                  border: `1px solid ${C.lineSoft}`,
                  borderRadius: 12,
                }}
              >
                <div style={{ fontSize: 14.5, fontWeight: 600, color: C.textSoft }}>
                  Nada encontrado para “{state.search.query.trim()}”.
                </div>
                <div
                  style={{ marginTop: 5, fontSize: 13, lineHeight: 1.55, color: C.textDim }}
                >
                  Tente um trecho menor, confira a acentuação ou busque por outra palavra da
                  mensagem.
                </div>
              </div>
            )}

          {state.search.results.map(message => {
            const author = userById(state, message.authorId);
            const preview = plainPreview(message.body, 180);
            return (
              <button
                key={message.id}
                type="button"
                className="hov-bg-1f"
                onClick={() => jump(message)}
                style={{
                  width: "100%",
                  display: "block",
                  marginBottom: 6,
                  padding: "10px 12px",
                  border: `1px solid ${C.lineSoft}`,
                  borderRadius: 10,
                  cursor: "pointer",
                  textAlign: "left",
                  background: "transparent",
                  color: "inherit",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontFamily: FONT_MONO,
                    fontSize: 10,
                    color: C.textLabel,
                  }}
                >
                  <span style={{ color: "var(--ac, #1F4FD8)" }}>{labelOf(message.channelKey)}</span>
                  <span>{formatStamp(message.createdAt)}</span>
                </span>
                <span
                  style={{
                    display: "block",
                    marginTop: 4,
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: colorOf(state, message.authorId),
                  }}
                >
                  {author?.name ?? message.authorId}
                </span>
                <span
                  style={{
                    display: "block",
                    marginTop: 2,
                    fontSize: 13.5,
                    lineHeight: 1.5,
                    color: C.textSoft,
                    overflowWrap: "break-word",
                  }}
                >
                  {preview ||
                    (message.attachments.length > 0
                      ? message.attachments.map(item => item.name).join(", ")
                      : "Mensagem sem texto")}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div style={{ position: "relative", flex: 1, minHeight: 0, display: "flex" }}>
          <div
            ref={msgsRef}
            onScroll={onScroll}
            style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: "18px 20px 8px" }}
          >
            {carregandoAntigas && (
              <div
                style={{
                  padding: "10px 4px 14px",
                  textAlign: "center",
                  color: C.textSoft,
                  fontSize: 12,
                }}
              >
                Carregando mensagens anteriores…
              </div>
            )}
            {showIntro && (
              <div
                style={{
                  padding: "6px 4px 18px",
                  borderBottom: `1px solid ${C.lineSoft}`,
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: 14,
                    background: C.lineSoft,
                    color: C.textSoft,
                    marginBottom: 12,
                    overflow: "hidden",
                  }}
                >
                  {isDm && dmUser ? (
                    dmUser.avatarUrl ? (
                      <img
                        src={dmUser.avatarUrl}
                        alt=""
                        style={{ width: 52, height: 52, objectFit: "cover", display: "block" }}
                      />
                    ) : (
                      <span style={{ fontSize: 20, fontWeight: 700, color: dmColor }}>
                        {initialOf(dmUser.name)}
                      </span>
                    )
                  ) : (
                    <HashIcon size={24} color={C.textSoft} />
                  )}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em" }}>
                  {introTitle}
                </div>
                <div style={{ marginTop: 5, fontSize: 13, color: C.textDim }}>{introSub}</div>
              </div>
            )}

            {showIntro && messages.length === 0 && (
              <div style={{ padding: "10px 4px 6px" }}>
                <div
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: 9.5,
                    letterSpacing: "0.12em",
                    color: C.textLabel,
                  }}
                >
                  {isDm ? "CONVERSA VAZIA" : "CANAL VAZIO"}
                </div>
                <div
                  style={{ marginTop: 8, fontSize: 14.5, fontWeight: 600, color: C.textSoft }}
                >
                  Nenhuma mensagem por aqui ainda.
                </div>
                <div
                  style={{ marginTop: 4, fontSize: 13, lineHeight: 1.55, color: C.textDim }}
                >
                  {emptyHint}
                </div>
              </div>
            )}

            {messages.map((message, index) => {
              const previous = index > 0 ? messages[index - 1] : null;
              const showHeader =
                previous === null ||
                previous.authorId !== message.authorId ||
                Boolean(message.replyToId) ||
                index === dividerIndex;
              return (
                <Fragment key={message.id}>
                  {index === dividerIndex && <NewMessagesLine />}
                  <MessageItem
                    message={message}
                    showHeader={showHeader}
                    highlight={highlightId === message.id}
                  />
                </Fragment>
              );
            })}
          </div>

          {!atBottom && (
            <button
              type="button"
              className="hov-bg-24"
              onClick={scrollToEnd}
              title={hasNew ? "Ir para as mensagens novas" : "Voltar ao fim da conversa"}
              style={{
                position: "absolute",
                left: "50%",
                bottom: 12,
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 14px",
                border: hasNew ? `1px solid ${hexA(C.red, 0.55)}` : `1px solid ${C.lineStrong}`,
                borderRadius: 999,
                cursor: "pointer",
                background: C.raised,
                color: hasNew ? NEW_RED : C.textSoft,
                fontFamily: FONT_MONO,
                fontSize: 9.5,
                letterSpacing: "0.12em",
                whiteSpace: "nowrap",
                boxShadow: "0 10px 26px rgba(0,0,0,.45)",
                zIndex: 3,
                animation: "pop 140ms ease",
              }}
            >
              <ArrowDownGlyph />
              {jumpLabel}
            </button>
          )}
        </div>
      )}

      {!searchOpen && typingLabel !== "" && (
        <div
          style={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            gap: 7,
            height: 18,
            padding: "0 24px",
            fontSize: 11.5,
            color: C.textDim,
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: "var(--ac, #1F4FD8)",
              animation: "blinkdot 1.2s ease-in-out infinite",
            }}
          />
          {typingLabel}
        </div>
      )}

      {!searchOpen && <Composer placeholder={placeholder} />}
    </>
  );
}
